// The World Editor in the online IDE (docs/ONLINE_IDE_FEASIBILITY.md,
// Phase 5). The editor is a Blitz program (_release/samples/world-editor),
// which the site holds compiled (tools/build_world_editor.mjs). It runs in the
// run frame like any program, over the project's files, with its own icons
// and guide under /b3d, its blitzpath. Each file it saves in the project comes
// back to the IDE (runner.js), which keeps it in the project.
//
// Editing a running program's world (DebugWorld) needs the desktop editor: a
// web page cannot reach another program.

import { versioned } from "./site.js";

export class WorldEditor {
  constructor(base) {
    this.base = base;
    this.files = null;
  }

  // The editor's program and files, fetched once.
  load() {
    this.files ??= Promise.all(["program.wasm", "tool.b3dpak"].map(async (name) => {
      const response = await fetch(versioned(new URL(name, this.base)));
      if (!response.ok) throw new Error(`The World Editor's ${name} is missing (${response.status})`);
      return response.arrayBuffer();
    })).then(([wasm, pak]) => ({ wasm, pak }), (error) => {
      this.files = null;
      throw error;
    });
    return this.files;
  }

  // What the runner needs to run the editor on a world in the project, by its
  // path there, or on a new world. program is the path, as the editor sees
  // it, of the project's Run file, its Your Program; or, with chosen, the
  // program its Test played last, which it keeps (editor_play.bb). The IDE
  // runs what Test asks for (main.js, playFromWorldEditor), so the editor is
  // told it can.
  async run(path, program = "", chosen = false) {
    const { wasm, pak } = await this.load();
    const slash = path ? path.lastIndexOf("/") : -1;
    const options = ["--play-host"];
    if (program) options.push("--program", `"${program}"`);
    if (program && chosen) options.push("--play-chosen");
    return {
      title: "World Editor",
      wasm: wasm.slice(0),
      // Saving starts beside the world, or in the project's folder.
      launchDirectory: slash < 0 ? "." : path.slice(0, slash),
      tool: {
        commandLine: [...(path ? [`"/app/${path}"`] : []), ...options].join(" "),
        assets: [{ root: "/b3d", assets: pak.slice(0) }],
        // The editor finds its icons and guide beneath blitzpath, and keeps
        // its settings (panel sizes, grid) under LOCALAPPDATA, which the
        // browser keeps for the IDE's World Editor.
        environment: { blitzpath: "/b3d", LOCALAPPDATA: "/home/local" },
      },
    };
  }

  // The shared asset library: the editor's Shared page on its own (library
  // mode, shared_page.bb), whose Add to project saves into the project's
  // folder, models/ and pictures/. The IDE keeps what it saves, as it keeps
  // the editor's worlds, and sends it the library's token (library.js).
  async library() {
    const { wasm, pak } = await this.load();
    return {
      title: "Asset Library",
      wasm: wasm.slice(0),
      launchDirectory: ".",
      tool: {
        commandLine: "--library --project /app",
        assets: [{ root: "/b3d", assets: pak.slice(0) }],
        environment: { blitzpath: "/b3d", LOCALAPPDATA: "/home/local" },
      },
    };
  }

  // The source of one of the players the editor's Play offers (FPS.bb and
  // the rest, in _release/samples/world-editor/play), which the site keeps
  // beside the editor (tools/build_world_editor.mjs).
  async player(name) {
    const response = await fetch(versioned(new URL(`play/${encodeURIComponent(name)}`, this.base)));
    if (!response.ok) throw new Error(`The World Editor's ${name} is missing (${response.status})`);
    return response.text();
  }
}

// Files an editor writes that are not the user's: temporary files it
// replaces its saves with.
export const temporaryFile = (path) => /(^|\/)\.|\.tmp(\.|$)|~$/i.test(path);

// The program that plays a world with a player and runs the world's own
// behaviours, by its runner, behaviours.bb (docs/WEB_CODE_BEHAVIOURS_DESIGN.md):
// beside the player in .world-editor (main.js, playFromWorldEditor), as
// behaviours/play.bb is beside the world on the desktop
// (scene_behaviour_code.bb's WacBehaviourCodePlayer). The compiler includes
// a file once, so the player's own Include of play_common.bb is skipped.
export function playWithBehaviours(player, runner) {
  return [
    `; Plays the world with ${player} and runs its own behaviours. The online IDE writes this for Play.`,
    "",
    'Include "play_common.bb"',
    `Include "../${runner}"`,
    "",
    "Type PlayBehaviours Implements PlayExtra",
    "\tMethod Update(world:Int)",
    "\t\tUpdateBehaviours world",
    "\tEnd Method",
    "\tMethod Finish(world:Int)",
    "\t\tFinishBehaviours world",
    "\tEnd Method",
    "End Type",
    "gPlayExtra = New PlayBehaviours",
    "",
    `Include "${player}"`,
    ""].join("\n");
}
